import type {
  TradeRowResponse,
  TradeSections,
} from '@/entities/business-relationship/api/businessRelationship.api';
import type { RelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import type { BadgeProps } from '@/shared/components/ui/badge';

/**
 * M-19 · M-20 거래 정보 드로어의 행 — 의원급(제약사)과 병원급(의약품)을 같은 모양으로 맞춘다.
 * 섹션(거래중 · 필터링 신청 · 종료·거절)과 표시 상태는 서버가 정한 값을 그대로 쓴다.
 */
export interface TradeRow {
  key: string;
  group: RelationshipGroup;
  /** 의원급: 제약사명 / 병원급: 의약품명 */
  title: string;
  /** 병원급만 — 날짜 줄 앞의 제약사명 */
  pharmaCompanyName: string | null;
  /** 서버 statusView (거래중 · 신청중 · 확인 중 · 이관 확인 중 · 거래 불가) */
  badgeLabel: string;
  badgeTone: BadgeProps['tone'];
  /** 행에 표시하는 날짜와 그 뒤에 붙는 말(승인 · 신청 · 종료). 값이 없으면 null */
  date: string | null;
  dateSuffix: string;
  minPrescriptionAmount: number | null;
  /** 이관 확인 중 — 금액 대신 「타 CSO 이관 확인 중」 */
  transferPending: boolean;
  /** 병원급 의약품 행만. RU 코드("-")는 null로 바꿔 M-12로 열지 않는다 */
  drugCode: string | null;
}

/** 신청 쪽 배지는 Figma대로 모두 파란색이다 */
const toneOf = (group: RelationshipGroup): BadgeProps['tone'] => {
  if (group === 'ongoing') return 'success';
  if (group === 'applying') return 'primary';
  return 'muted';
};

/** 섹션별 날짜 — 병원급은 의약품 날짜를 먼저, 없으면 제약사 날짜 */
const dateOf = (
  group: RelationshipGroup,
  row: TradeRowResponse,
): { date: string | null; dateSuffix: string } => {
  if (group === 'ongoing')
    return {
      date: row.drugApprovalDate ?? row.approvalDate,
      dateSuffix: '승인',
    };
  if (group === 'applying')
    return {
      date: row.drugApplicationDate ?? row.applicationDate,
      dateSuffix: '신청',
    };
  return { date: row.terminatedAt, dateSuffix: '종료' };
};

const toTradeRow = (
  row: TradeRowResponse,
  group: RelationshipGroup,
  isHospital: boolean,
): TradeRow => {
  const drugCode = row.drugCode && row.drugCode !== '-' ? row.drugCode : null;
  return {
    key: row.tradeId,
    group,
    title: isHospital
      ? (row.drugName ?? row.drugCode ?? '—')
      : row.pharmaCompanyName,
    pharmaCompanyName: isHospital ? row.pharmaCompanyName : null,
    badgeLabel: row.statusView,
    badgeTone: toneOf(group),
    ...dateOf(group, row),
    minPrescriptionAmount: row.minPrescriptionAmount,
    transferPending: row.transactionStatus === 'PENDING_TRANSFER',
    drugCode,
  };
};

/** 서버 섹션 → 드로어 섹션별 행. 정렬은 서버 순서 그대로 */
export const toTradeSections = (
  sections: TradeSections,
  isHospital: boolean,
): Record<RelationshipGroup, TradeRow[]> => ({
  ongoing: sections.ongoing.map((r) => toTradeRow(r, 'ongoing', isHospital)),
  applying: sections.applying.map((r) => toTradeRow(r, 'applying', isHospital)),
  closed: sections.closed.map((r) => toTradeRow(r, 'closed', isHospital)),
});
